// M129/M131 + M135-2 + M135-5 — SEO-overzicht + concrete oplossingsvoorstel per advies.
// M135-2 (root) — variantkeuze-modal (title + meta) vóór "Zet klaar".
// M135-5 (root) — "Blogconcept klaarzetten" alleen op rising_query &
//                 query_no_matching_page; toast + navigatie bij bestaand.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { Loader2, ExternalLink, Sparkles, Send, Check, X } from "lucide-react";

const NUM = new Intl.NumberFormat("nl-NL");

// M135-5 — scope voor "Blogconcept klaarzetten"-knop (backend enforceert
// dit óók server-side).
const _BLOG_CONCEPT_RULES = new Set([
  "gsc.rising_query", "gsc.query_no_matching_page",
]);

function ProposedSolutionView({ solution }) {
  const p = solution?.payload || {};
  const kind = p.kind;
  if (!kind) {
    return (
      <pre className="text-[11px] text-text-muted whitespace-pre-wrap bg-bg-canvas rounded p-2 max-h-48 overflow-auto">
        {JSON.stringify(p, null, 2)}
      </pre>
    );
  }
  const H = ({ label }) => (
    <div className="text-[9px] text-text-muted mt-2 mb-1">{label}</div>
  );
  return (
    <div className="text-[12px] text-text-primary space-y-1">
      {p.why_nl && (
        <>
          <H label="Waarom" /><div className="text-text-muted italic">{p.why_nl}</div>
        </>
      )}
      {kind === "title_meta_alternatives" && (
        <>
          <H label="Title-tag voorstellen" />
          <ul className="list-disc list-inside space-y-0.5">
            {(p.title_alternatives || []).map((t, i) => (<li key={i}>{t}</li>))}
          </ul>
          <H label="Meta-omschrijving voorstellen" />
          <ul className="list-disc list-inside space-y-0.5">
            {(p.meta_description_alternatives || []).map((t, i) => (<li key={i}>{t}</li>))}
          </ul>
        </>
      )}
      {kind === "content_expansion" && (
        <>
          <H label="Content-uitbreiding" />
          <div className="whitespace-pre-wrap">{p.content_expansion_nl}</div>
          <H label="Interne links" />
          <ul className="list-disc list-inside space-y-0.5">
            {(p.internal_link_suggestions || []).map((l, i) => (
              <li key={i}><span className="text-text-muted">van</span> {l.from_page_nl} <span className="text-text-muted">— anker:</span> "{l.anchor_nl}"</li>
            ))}
          </ul>
        </>
      )}
      {kind === "momentum_boost" && (
        <>
          <H label="Content-uitbreiding" />
          <div className="whitespace-pre-wrap">{p.content_expansion_nl}</div>
          {p.optional_blog_outline?.title_nl && (
            <>
              <H label="Optionele blog-outline" />
              <div className="font-semibold">{p.optional_blog_outline.title_nl}</div>
              <ul className="list-disc list-inside space-y-0.5">
                {(p.optional_blog_outline.h2s || []).map((h, i) => (<li key={i}>{h}</li>))}
              </ul>
            </>
          )}
        </>
      )}
      {kind === "new_landing_page" && (
        <>
          <H label="Titel" /><div className="font-semibold">{p.page_title_nl}</div>
          <H label="Meta-omschrijving" /><div className="text-text-muted italic">{p.meta_description_nl}</div>
          <H label="H1" /><div>{p.h1_nl}</div>
          <H label="H2's" />
          <ul className="list-disc list-inside space-y-0.5">
            {(p.h2s || []).map((h, i) => (<li key={i}>{h}</li>))}
          </ul>
          {p.body_hints_nl && (
            <>
              <H label="Body-hints" />
              <ul className="list-disc list-inside space-y-0.5">
                {p.body_hints_nl.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </>
          )}
          <H label="URL-slug" /><code className="text-accent-teal text-xs">{p.url_slug}</code>
        </>
      )}
    </div>
  );
}

// M135-2 — variantkeuze-modal. Alleen actief voor
// `kind==="title_meta_alternatives"` (meerdere titels + metas). Voor
// andere kinds slaan we deze modal over.
function VariantChoiceModal({ open, onClose, solution, onConfirm, busy }) {
  const p = solution?.payload || {};
  const titles = p.title_alternatives || [];
  const metas = p.meta_description_alternatives || [];
  const [chosenTitle, setChosenTitle] = useState(titles[0] || "");
  const [chosenMeta, setChosenMeta] = useState(metas[0] || "");
  useEffect(() => {
    if (open) {
      setChosenTitle(titles[0] || "");
      setChosenMeta(metas[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid="seo-variant-modal">
      <div className="bg-bg-panel border border-border-hairline rounded-card max-w-2xl w-full p-5 max-h-[85vh] overflow-auto">
        <div className="text-[10px] text-text-muted mb-2">
          Kies één variant vóór klaarzetten
        </div>
        <div className="text-sm font-semibold text-text-primary mb-3">
          Zo landt het voorstel in Publish Queue
        </div>
        <div className="mb-4">
          <div className="text-[10px] text-text-muted mb-1">
            Kies titel
          </div>
          <div className="space-y-1">
            {titles.map((t, i) => (
              <label key={i}
                      data-testid={`seo-variant-title-${i}`}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-[12px] ${ chosenTitle === t ? "border-accent-teal/60 bg-accent-teal/5" : "border-border-hairline hover:border-accent-teal/30"}`}>
                <input type="radio" name="title-choice"
                        className="mt-0.5 accent-accent-teal"
                        checked={chosenTitle === t}
                        onChange={() => setChosenTitle(t)} />
                <span className="text-text-primary">{t}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-[10px] text-text-muted mb-1">
            Kies meta-omschrijving
          </div>
          <div className="space-y-1">
            {metas.map((m, i) => (
              <label key={i}
                      data-testid={`seo-variant-meta-${i}`}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-[12px] ${ chosenMeta === m ? "border-accent-teal/60 bg-accent-teal/5" : "border-border-hairline hover:border-accent-teal/30"}`}>
                <input type="radio" name="meta-choice"
                        className="mt-0.5 accent-accent-teal"
                        checked={chosenMeta === m}
                        onChange={() => setChosenMeta(m)} />
                <span className="text-text-primary">{m}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="seo-variant-cancel"
                  className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-muted disabled:opacity-50">
            Annuleren
          </button>
          <button onClick={() => onConfirm({ chosen_title: chosenTitle, chosen_meta_description: chosenMeta })}
                  disabled={busy || !chosenTitle || !chosenMeta}
                  data-testid="seo-variant-confirm"
                  className="text-[11px] px-3 py-1 rounded border border-accent-teal/40 bg-accent-teal/10 text-accent-teal inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Zet klaar voor uitvoering
          </button>
        </div>
      </div>
    </div>
  );
}

function AdviceCard({ card, onToast }) {
  const [busy, setBusy] = useState(null); // "gen" | "queue" | "queue_blog" | null
  const [err, setErr] = useState(null);
  const [solution, setSolution] = useState(card.proposed_solution || null);
  const [queuedId, setQueuedId] = useState(card.queued_proposal_id || null);
  const [showDetail, setShowDetail] = useState(false);
  const [showVariant, setShowVariant] = useState(false);

  // M135-9 — bestaand blogconcept via server-lookup. Als aanwezig,
  // tonen we "Bekijk blogconcept" i.p.v. de generatie-knop. Voorkomt
  // dubbele records + onnodige LLM-calls.
  const existingBlogId = card.existing_blog_proposal_id
    || card.queued_blog_proposal_id
    || null;
  // Generatie-knop alleen voor rules die semantisch een nieuw blog-
  // concept produceren én waar nog géén bestaand concept ligt.
  const canGenerateBlog = !card.is_placeholder
    && !existingBlogId
    && _BLOG_CONCEPT_RULES.has(card.advice_rule_id);

  const gen = async () => {
    setBusy("gen"); setErr(null);
    try {
      const { data } = await api.post(`/seo/advice/generate-solution`, { identity_key: card.identity_key });
      setSolution(data.proposed_solution);
    } catch (e) {
      setErr(e?.response?.data?.detail?.reason_nl || e?.response?.data?.detail || "Oplossing genereren mislukt.");
    } finally { setBusy(null); }
  };

  const queueWith = async (extra = {}) => {
    setBusy("queue"); setErr(null);
    try {
      const { data } = await api.post(`/seo/advice/queue`,
        { identity_key: card.identity_key, ...extra });
      setQueuedId(data.proposal_id);
      setShowVariant(false);
      onToast?.({ ok: true,
                    message_nl: data.toast_nl
                      || "Voorstel staat in Publish Queue voor goedkeuring." });
    } catch (e) {
      setErr(e?.response?.data?.detail?.reason_nl || e?.response?.data?.detail || "Klaarzetten mislukt.");
    } finally { setBusy(null); }
  };

  const onQueueClick = () => {
    const kind = solution?.payload?.kind;
    // Variantkeuze alleen als title/meta-alternatieven aanwezig zijn.
    if (kind === "title_meta_alternatives") {
      setShowVariant(true);
      return;
    }
    queueWith();
  };

  // M135-5 — Blogconcept klaarzetten. Idempotent server-side. Bij
  // bestaand concept: existed=true + navigatie naar `?open=<id>`.
  const queueBlog = async () => {
    setBusy("queue_blog"); setErr(null);
    try {
      const { data } = await api.post(`/seo/advice/queue-blogpost`,
        { identity_key: card.identity_key });
      if (!data?.ok || !data?.proposal_id) {
        const msg = data?.error_nl || data?.reason_nl
          || "Er ging iets mis bij het klaarzetten van het concept.";
        setErr(msg);
        onToast?.({ ok: false, message_nl: msg });
        return;
      }
      const msg = data.message_nl || data.toast_nl
        || (data.existed
              ? "Er staat al een concept voor dit onderwerp klaar. We openen het voor je."
              : "Concept klaargezet in Publish Queue.");
      onToast?.({ ok: true, message_nl: msg });
      const to = data.redirect_to || `/publish-queue?open=${data.proposal_id}`;
      // Korte delay zodat de toast zichtbaar wordt vóór navigatie.
      setTimeout(() => { window.location.href = to; }, 500);
    } catch (e) {
      const msg = e?.response?.data?.detail?.reason_nl
        || e?.response?.data?.error_nl
        || e?.response?.data?.detail
        || "Er ging iets mis bij het klaarzetten van het concept.";
      setErr(typeof msg === "string" ? msg : "Er ging iets mis.");
      onToast?.({ ok: false, message_nl: (typeof msg === "string" ? msg : "Er ging iets mis.") });
    } finally { setBusy(null); }
  };

  const canGen = !card.is_placeholder;
  return (
    <div data-testid={`seo-card-${card.advice_rule_id}`}
          className={`rounded-md border p-4 ${card.is_placeholder ? "border-warn-amber/40 border-dashed bg-warn-amber/5" : "border-border-hairline bg-bg-panel"}`}>
      {card.is_placeholder && (
        <div className="text-[9px] text-warn-amber font-semibold mb-1">Voorbeeldweergave</div>
      )}
      <div className="text-sm font-semibold text-text-primary mb-1">{card.title}</div>
      {card.target_entity_id && (
        <div className="text-[11px] text-text-muted mb-2 break-all">
          <span className="font-mono">{card.target_entity_id}</span>
        </div>
      )}
      {(card.body || {}).wat_ik_zie && (
        <div className="text-xs text-text-muted mb-3 leading-relaxed">{(card.body || {}).wat_ik_zie}</div>
      )}

      {solution && (
        <div className="mt-2 mb-3 rounded border border-accent-teal/30 bg-accent-teal/5 p-3" data-testid={`seo-card-solution-${card.advice_rule_id}`}>
          <div className="text-[9px] text-accent-teal font-semibold mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Concrete oplossing
          </div>
          <ProposedSolutionView solution={solution} />
        </div>
      )}

      {err && <div className="text-[11px] text-warn-amber italic mb-2" data-testid={`seo-card-error-${card.advice_rule_id}`}>{err}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowDetail(true)} data-testid={`seo-card-detail-${card.advice_rule_id}`}
                 className="text-[11px] px-2 py-1 rounded border border-border-hairline hover:border-accent-teal/40 text-text-primary flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Bekijk detail
        </button>
        {canGen && !solution && (
          <button onClick={gen} disabled={busy === "gen"} data-testid={`seo-card-generate-${card.advice_rule_id}`}
                   className="text-[11px] px-2 py-1 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal flex items-center gap-1 disabled:opacity-50">
            {busy === "gen" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Genereer oplossing
          </button>
        )}
        {canGen && solution && !queuedId && (
          <button onClick={onQueueClick} disabled={busy === "queue"} data-testid={`seo-card-queue-${card.advice_rule_id}`}
                   title="Voorstel wordt klaargezet. Publiceren gebeurt handmatig via je CMS na jouw goedkeuring."
                   className="text-[11px] px-2 py-1 rounded border border-pos-green/40 hover:bg-pos-green/10 text-pos-green flex items-center gap-1 disabled:opacity-50">
            {busy === "queue" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Zet klaar voor uitvoering
          </button>
        )}
        {existingBlogId && !card.is_placeholder && (
          <a href={`/publish-queue?open=${existingBlogId}`}
              data-testid={`seo-card-view-blogpost-${card.advice_rule_id}`}
              title="Er staat al een blogconcept klaar voor dit onderwerp."
              className="text-[11px] px-2 py-1 rounded border border-accent-teal/60 bg-accent-teal/10 hover:bg-accent-teal/20 text-accent-teal flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Bekijk blogconcept
          </a>
        )}
        {canGenerateBlog && (
          <button onClick={queueBlog} disabled={busy === "queue_blog"}
                   data-testid={`seo-card-queue-blogpost-${card.advice_rule_id}`}
                   title="Genereert of hergebruikt een blogpost-concept voor dit signaal (geen live publicatie)."
                   className="text-[11px] px-2 py-1 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal flex items-center gap-1 disabled:opacity-50">
            {busy === "queue_blog" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Blogconcept klaarzetten
          </button>
        )}
        {canGen && solution && (
          <button onClick={gen} disabled={busy === "gen"} data-testid={`seo-card-regen-${card.advice_rule_id}`}
                   className="text-[11px] px-2 py-1 rounded border border-border-hairline text-text-muted hover:text-text-primary flex items-center gap-1 disabled:opacity-50">
            {busy === "gen" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Opnieuw genereren
          </button>
        )}
        {queuedId && (
          <span className="text-[11px] text-pos-green flex items-center gap-1" data-testid={`seo-card-queued-${card.advice_rule_id}`}>
            <Check className="w-3 h-3" /> Klaargezet in Publish Queue
          </span>
        )}
      </div>

      <VariantChoiceModal open={showVariant}
                            onClose={() => setShowVariant(false)}
                            solution={solution}
                            busy={busy === "queue"}
                            onConfirm={queueWith} />

      {showDetail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="seo-detail-modal">
          <div className="bg-bg-panel border border-border-hairline rounded-card max-w-lg w-full p-5 max-h-[80vh] overflow-auto">
            <div className="text-[10px] text-text-muted mb-2">Detail — {card.advice_rule_id}</div>
            <div className="text-sm font-semibold text-text-primary mb-2">{card.title}</div>
            {Object.entries(card.body || {}).map(([k, v]) => (
              <div key={k} className="text-[11px] mb-2"><span className="text-text-muted text-[9px]">{k}</span><div className="text-text-primary whitespace-pre-wrap">{String(v)}</div></div>
            ))}
            <button onClick={() => setShowDetail(false)} data-testid="seo-detail-close"
                    className="mt-3 text-[11px] px-3 py-1 rounded border border-border-hairline text-text-primary">Sluiten</button>
          </div>
        </div>
      )}
    </div>
  );
}

// M135-5 — kleine toast-strip bovenaan pagina, verdwijnt na 4s.
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => onClose(), 4200);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const isOk = toast.ok !== false;
  return (
    <div data-testid="seo-toast"
          className={`fixed top-4 right-4 z-[70] rounded-md border px-4 py-2 text-[12px] shadow-lg max-w-md ${ isOk ? "border-pos-green/40 bg-pos-green/10 text-pos-green" : "border-warn-amber/40 bg-warn-amber/10 text-warn-amber"}`}>
      {toast.message_nl}
    </div>
  );
}

export default function SeoOverviewPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/seo/overview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "SEO-overzicht niet beschikbaar.");
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (err) return <AppLayout><div className="text-text-muted text-sm" data-testid="seo-page-error">{err}</div></AppLayout>;
  if (!data) return <AppLayout><div className="text-text-muted text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> SEO-overzicht laden…</div></AppLayout>;
  const h = data.header || {};
  const cards = Array.isArray(data.advice_cards) ? data.advice_cards : [];
  return (
    <AppLayout>
      <Toast toast={toast} onClose={() => setToast(null)} />
      {!hideInnerHeader && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary tracking-tight" data-testid="seo-page-title">SEO — Search Console</h1>
          <p className="text-sm text-text-muted mt-1">Zichtbaarheid over de laatste 28 dagen — adviesmodus.</p>
        </div>
      )}

      {h.available ? (
        <section className="mb-6 rounded-card border border-border-hairline bg-bg-panel-raised p-5" data-testid="seo-header">
          <div className="text-[10px] text-text-subtle mb-2">
            {h.period_start} t/m {h.period_end} · laatste datum met gegevens {h.last_date_with_data}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-[10px] text-text-muted">Klikken</div><div className="text-2xl tabular-nums text-text-primary" data-testid="seo-clicks">{NUM.format(h.clicks)}</div></div>
            <div><div className="text-[10px] text-text-muted">Vertoningen</div><div className="text-2xl tabular-nums text-text-primary" data-testid="seo-impressions">{NUM.format(h.impressions)}</div></div>
            <div><div className="text-[10px] text-text-muted">CTR</div><div className="text-2xl tabular-nums text-text-primary" data-testid="seo-ctr">{h.ctr_pct}%</div></div>
            <div><div className="text-[10px] text-text-muted">Gem. positie</div><div className="text-2xl tabular-nums text-text-primary" data-testid="seo-position">{h.avg_position}</div></div>
          </div>
        </section>
      ) : (
        <section className="mb-6 rounded-card border border-border-hairline bg-bg-panel-raised p-5 text-sm text-text-muted italic" data-testid="seo-header-empty">
          Search Console is nog niet gekoppeld of levert nog geen gegevens.
        </section>
      )}

      <section data-testid="seo-advice-cards">
        <h2 className="text-[11px] text-text-muted font-semibold mb-3">SEO-adviezen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cards.map((c) => (
            <AdviceCard key={c.identity_key} card={c} onToast={setToast} />
          ))}
        </div>
        {data.note_nl && <p className="mt-3 text-[11px] text-text-subtle italic">{data.note_nl}</p>}
      </section>
    </AppLayout>
  );
}
